import { Button, Card, CardContent, CardHeader, CardTitle } from "@prelude-ai/design-system"
import { createFileRoute, useSearch } from "@tanstack/react-router"
import { z } from "zod"
import { HAS_PRELUDE_SESSION, PRELUDE_API_URL, USE_STAGING } from "../config"
import googleIcon from "../icons/login-google.png"
import microsoftIcon from "../icons/login-microsoft.png"
import paintLogo from "../icons/paint-logo.png"

const loginSearchSchema = z.object({
  error: z.string().optional()
})

type LoginProvider = "google" | "microsoft"

const providerIcons: Record<LoginProvider, string> = {
  google: googleIcon,
  microsoft: microsoftIcon
}

// The API runs the full OAuth flow (including its own /:provider/callback),
// sets the sessionId cookie and redirects back to redirect_uri.
const handleLogin = (provider: LoginProvider) => {
  const redirectUri = encodeURIComponent(`${window.location.origin}/`)
  window.location.assign(`${PRELUDE_API_URL}/api/v1/auth/${provider}/login?redirect_uri=${redirectUri}`)
}

function LoginButton({ provider, label }: { provider: LoginProvider; label: string }) {
  return (
    <Button
      variant="outline"
      className="h-auto w-full justify-center gap-2 py-3 shadow-xs"
      onClick={() => handleLogin(provider)}
    >
      <img src={providerIcons[provider]} alt="" aria-hidden className="size-5 object-contain" />
      {label}
    </Button>
  )
}

function Login() {
  const { error } = useSearch({ from: "/login" })

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 font-sans">
      <Card className="w-full max-w-md gap-5 px-8 py-10 shadow-xs">
        <CardHeader className="gap-5 text-center">
          <img src={paintLogo} alt="Logo" className="mx-auto min-w-17 w-17 h-auto shrink-0" />
          <CardTitle className="text-3xl">Sign in to Agent Studio</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 p-0">
          {USE_STAGING ? (
            !HAS_PRELUDE_SESSION && (
              <p className="px-2 py-3 text-center text-sm text-muted-foreground">
                Staging mode — sign in using "<code>prelude-ctl create-session</code>", then set{" "}
                <code>PRELUDE_SESSION</code> in <code>.env</code> to your <code>sessionId</code> cookie value and
                restart the dev server.
              </p>
            )
          ) : (
            <>
              <LoginButton provider="google" label="Continue with Google" />
              <LoginButton provider="microsoft" label="Continue with Microsoft" />
            </>
          )}
          {error && (
            <p role="alert" className="text-center text-sm text-destructive">
              {error}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/login")({
  component: Login,
  validateSearch: loginSearchSchema,
  shouldReload: true
})
